import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { ApiError } from '@/api/client'
import type { ScanResult } from '@/features/scan/api'

import { resolveForShelf } from './resolve'

/** Resolve a code and go where it leads on the shelf. */
export function useShelfScan() {
  const navigate = useNavigate()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sheet, setSheet] = useState<ScanResult | null>(null)

  async function handle(code: string) {
    setPending(true)
    setError(null)
    try {
      const target = await resolveForShelf(code)
      const scanned = code.trim().toUpperCase()
      switch (target.kind) {
        case 'drawer':
          await navigate({
            to: '/shelf/drawer/$locationId',
            params: { locationId: target.drawerId },
            search: { lot: target.lotId, scanned },
          })
          return
        case 'home':
          await navigate({ to: '/shelf', search: { q: target.q } })
          return
        case 'part':
          await navigate({
            to: '/parts/$partId',
            params: { partId: target.partId },
            search: { lot: target.lotId },
          })
          return
        case 'sheet':
          setSheet(target.result)
          return
        default:
          setError(`Nothing has the code ${scanned}.`)
      }
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.problem.detail : 'That code could not be looked up.',
      )
    } finally {
      setPending(false)
    }
  }

  return { handle, pending, error, sheet, closeSheet: () => setSheet(null) }
}
